import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Alert, Badge, Button, Card, Form, Modal, Spinner, Table } from 'react-bootstrap'
import useFetch from '../../_fetch'
import { useAuth } from '../../auth/auth-context'

type ApiAccess = { enabled: boolean; suspended: boolean; can_manage_keys: boolean; available_scopes: string[]; max_active_keys: number; base_url: string; documentation_url: string }
type ApiKey = { id: string; name: string; scopes: string[]; created_at: string; last_used_at: string | null }
async function checked(response: Response) {
    if (!response.ok) {
        const text = await response.text()
        let message = text
        try { const body = JSON.parse(text); message = body.detail || body.message || text } catch { /* plain session-auth error */ }
        throw new Error(message || 'The request failed.')
    }
    return response.status === 204 ? null : response.json()
}
function timestamp(value: string | null) { return value ? new Date(value).toISOString().replace('T', ' ').replace('Z', ' UTC') : 'Never' }

// Remount on account/identity/role changes: an old asynchronous response cannot
// repopulate another account's key list or one-time secret.
export default function ApiSettings() {
    const { user } = useAuth()
    const context = [user?.username, user?._auth?.identityId, user?.accountAccessRole, user?.admin?.username, user?.switchedFrom?.username].join(':')
    return <ApiSettingsCard key={context} accountName={user?.username || 'Account'} />
}
export function ApiSettingsCard({ accountName }: { accountName: string }) {
    const { doFetch } = useFetch()
    const [access, setAccess] = useState<ApiAccess | null>(null)
    const [keys, setKeys] = useState<ApiKey[]>([])
    const [name, setName] = useState('')
    const [scopes, setScopes] = useState<string[]>(['network:read'])
    const [secret, setSecret] = useState('')
    const [revoke, setRevoke] = useState<ApiKey | null>(null)
    const [busy, setBusy] = useState(false)
    const [error, setError] = useState('')
    const [copied, setCopied] = useState('')
    const mounted = useRef(true)
    useEffect(() => { mounted.current = true; return () => { mounted.current = false } }, [])
    const load = useCallback(async () => {
        const status: ApiAccess = await checked(await doFetch('/user/api-access'))
        if (!mounted.current) return
        setAccess(status)
        setScopes((previous) => previous.filter((scope) => status.available_scopes.includes(scope)))
        if (!status.enabled || status.suspended || !status.can_manage_keys) setSecret('')
        const nextKeys = status.can_manage_keys ? await checked(await doFetch('/user/api-keys')) : []
        if (mounted.current) setKeys(nextKeys)
    }, [doFetch])
    useEffect(() => {
        const refresh = () => load().catch((reason) => { if (mounted.current) setError(reason.message) })
        refresh()
        const timer = window.setInterval(refresh, 30000)
        return () => window.clearInterval(timer)
    }, [load])
    const create = async (event: React.FormEvent) => {
        event.preventDefault()
        setBusy(true); setError(''); setSecret(''); setCopied('')
        try {
            const result = await checked(await doFetch('/user/api-keys', { name, scopes }))
            if (!mounted.current) return
            // Secret stays in component memory; never local/session storage or URLs.
            setSecret(result.key)
            setName('')
            await load()
        } catch (reason) {
            if (mounted.current) {
                setError(reason instanceof Error ? reason.message : 'Could not create the API key.')
                await load().catch(() => undefined)
            }
        } finally { if (mounted.current) setBusy(false) }
    }
    const remove = async () => {
        if (!revoke) return
        setBusy(true); setError('')
        try {
            await checked(await doFetch(`/user/api-keys/${encodeURIComponent(revoke.id)}`, undefined, 'DELETE'))
            if (!mounted.current) return
            setRevoke(null); setSecret('')
            await load()
        } catch (reason) {
            if (mounted.current) setError(reason instanceof Error ? reason.message : 'Could not revoke the API key.')
        } finally { if (mounted.current) setBusy(false) }
    }
    const copy = async (text: string, label: string) => {
        try { await navigator.clipboard.writeText(text); if (mounted.current) setCopied(label) }
        catch { if (mounted.current) setError('Copy failed. Select and copy the text manually.') }
    }
    return <Card className="user-settings-panel border-0">
        <Card.Header className="bg-white border-0 user-settings-card-header">
            <h5>API · {accountName}</h5>
            <p className="text-muted mb-0">Read network and Link Automation data from your own applications.</p>
        </Card.Header>
        <Card.Body>
            {error && <Alert variant="danger" role="alert">{error}</Alert>}
            {!access ? <div><Spinner size="sm" animation="border" /> Loading API access…</div> : <>
                <Badge bg={access.enabled && !access.suspended ? 'success' : 'secondary'} className="mb-3">
                    {access.suspended ? 'Temporarily suspended' : access.enabled ? 'API access enabled' : 'API access disabled'}
                </Badge>
                {!access.enabled && <Alert variant="info">API access is disabled by default. Contact your Nordpar administrator to enable it for this account.</Alert>}
                {access.suspended && <Alert variant="warning">This account is locked. API calls are suspended until a Nordpar administrator unlocks it.</Alert>}
                <p><a className="btn btn-outline-primary" href={access.documentation_url} target="_blank" rel="noopener noreferrer">Open API documentation</a></p>
                {!access.can_manage_keys ? <p className="mt-4 text-muted">Account managers and Nordpar administrators manage API keys.</p> : <>
                    <hr />
                    <h6>Account API keys ({keys.length}/{access.max_active_keys})</h6>
                    <p className="text-muted small">Keys belong to this account, have no expiry and can be revoked immediately. Changing a password does not revoke them.</p>
                    <Table responsive size="sm">
                        <thead><tr><th>Name</th><th>Scopes</th><th>Created</th><th>Last used</th><th>Action</th></tr></thead>
                        <tbody>{keys.map((key) => <tr key={key.id}>
                            <td className="text-break">{key.name}</td><td>{key.scopes.join(', ')}</td><td>{timestamp(key.created_at)}</td><td>{timestamp(key.last_used_at)}</td>
                            <td><Button variant="outline-danger" size="sm" disabled={busy} onClick={() => setRevoke(key)}>Revoke</Button></td>
                        </tr>)}{!keys.length && <tr><td colSpan={5}>No active keys.</td></tr>}</tbody>
                    </Table>
                    <Form onSubmit={create}>
                        <Form.Group controlId="api-key-name" className="mb-3">
                            <Form.Label>Key name</Form.Label>
                            <Form.Control value={name} onChange={(event) => setName(event.target.value)} maxLength={80} required placeholder="e.g. Accounting integration" disabled={busy || !access.enabled || access.suspended} />
                        </Form.Group>
                        <fieldset className="mb-3"><legend className="fs-6">Read scopes</legend>
                            {access.available_scopes.map((scope) => <Form.Check key={scope} id={`api-scope-${scope}`} label={scope === 'network:read' ? 'Network data' : 'Link Automation data'} checked={scopes.includes(scope)} disabled={busy || !access.enabled || access.suspended}
                                onChange={() => setScopes((previous) => previous.includes(scope) ? previous.filter((value) => value !== scope) : [...previous, scope])} />)}
                        </fieldset>
                        <Button type="submit" disabled={busy || !name.trim() || !scopes.length || !access.enabled || access.suspended || keys.length >= access.max_active_keys}>{busy ? 'Working…' : 'Create API key'}</Button>
                    </Form>
                </>}
            </>}
        </Card.Body>
        <Modal show={!!secret} onHide={() => { setSecret(''); setCopied('') }} backdrop="static">
            <Modal.Header closeButton><Modal.Title>Copy your API key now</Modal.Title></Modal.Header>
            <Modal.Body>
                <p>This secret is shown only once. Store it securely; anyone with it can read the selected account data.</p>
                <Form.Control as="textarea" readOnly value={secret} aria-label="New API key" rows={3} />
                <Button className="mt-3" onClick={() => copy(secret, 'Key copied')}>Copy API key</Button>
                {copied === 'Key copied' && <span className="ms-2" role="status">Key copied</span>}
            </Modal.Body>
            <Modal.Footer><Button variant="secondary" onClick={() => { setSecret(''); setCopied('') }}>Done</Button></Modal.Footer>
        </Modal>
        <Modal show={!!revoke} onHide={() => !busy && setRevoke(null)}>
            <Modal.Header closeButton><Modal.Title>Revoke API key</Modal.Title></Modal.Header>
            <Modal.Body>Revoke “{revoke?.name}”? Applications using this key will lose access immediately.</Modal.Body>
            <Modal.Footer><Button variant="secondary" disabled={busy} onClick={() => setRevoke(null)}>Cancel</Button><Button variant="danger" disabled={busy} onClick={remove}>Revoke key</Button></Modal.Footer>
        </Modal>
    </Card>
}
